import React, { useState, useEffect, useCallback, useMemo } from "react";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import "./apartmentSetup.css";

function ApartmentSetup({ propertyId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPhotos = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`/api/client-requests/properties/${propertyId}/setup-photos`);
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching setup photos:", error);
      setPhotos([]);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const images = useMemo(
    () =>
      photos.map((p) => ({
        original: p.file_url,
        thumbnail: p.file_url,
        description: p.description || undefined,
      })),
    [photos]
  );

  const openModal = (index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  // ✅ ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  return (
    <div className="aps">
      <div className="aps__head">
        <h2 className="aps__title">Apartment Setup Photos</h2>
      </div>

      {isLoading ? (
        <div className="aps__loading">
          <div className="app-loading">
            <div className="app-spinner" />
            <div className="app-spinner-text">Loading photos…</div>
          </div>
        </div>
      ) : photos.length > 0 ? (
        <div className="apsGrid">
          {photos.map((photo, index) => (
            <div className="apsThumbWrap" key={photo.id || index}>
              <button
                type="button"
                className="apsThumb"
                onClick={() => openModal(index)}
                title="Open photo"
              >
                <img src={photo.file_url} alt={`Setup ${index + 1}`} loading="lazy" />
                <span className="apsThumb__overlay" aria-hidden="true" />
              </button>
              {photo.description && (
                <div className="apsThumb__desc" title={photo.description}>{photo.description}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="apsEmpty">
          <FontAwesomeIcon icon={faCamera} className="apsEmpty__icon" />
          <div className="apsEmpty__title">No setup photos yet</div>
          <div className="apsEmpty__text">
            Share a few photos so our cleaners can restore the apartment to your preferred setup.
          </div>
        </div>
      )}

      {isOpen ? (
        <div className="apsModal" role="dialog" aria-modal="true" onClick={closeModal}>
          {/* ✅ close button outside / fixed to screen */}
          <button
            type="button"
            className="modal-x"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            aria-label="Close"
            title="Close"
          >
            <span aria-hidden="true">×</span>
          </button>

          {/* ✅ no white panel; keep clicks inside from closing */}
          <div className="apsModal__content" onClick={(e) => e.stopPropagation()}>
            <ImageGallery
              items={images}
              startIndex={currentIndex}
              showThumbnails={false}
              showFullscreenButton={false}
              showPlayButton={false}
              showBullets={false}
              showIndex={true}
              slideDuration={350}
              lazyLoad={true}
              showNav={true}
              renderLeftNav={(onClick, disabled) => (
                <button
                  type="button"
                  className="apsNav apsNav--left"
                  onClick={onClick}
                  disabled={disabled}
                  aria-label="Previous photo"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                    <path
                      d="M15 6l-6 6 6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              renderRightNav={(onClick, disabled) => (
                <button
                  type="button"
                  className="apsNav apsNav--right"
                  onClick={onClick}
                  disabled={disabled}
                  aria-label="Next photo"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                    <path
                      d="M9 6l6 6-6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ApartmentSetup;
